// src/services/accountingService.js
import { 
    collection, 
    query, 
    orderBy, 
    getDocs, 
    getDoc, 
    doc, 
    setDoc, 
    updateDoc, 
    deleteDoc, 
    where, 
    runTransaction, 
    serverTimestamp 
} from 'firebase/firestore';
import { 
    getStorage, 
    ref, 
    uploadBytes, 
    getDownloadURL 
} from 'firebase/storage';
import { db } from '../config/firebase';

const ACCOUNTING_COL = 'accounting';
const CLIENTS_COL = 'clients';
const PARAMS_COL = 'parameters';
const EXPENSES_COL = 'expenses';
const COUNTER_COL = 'accountingCounter';

export const accountingService = {
    /**
     * Obtiene todos los registros contables y los parámetros del sistema.
     */
    getAccountingData: async () => {
        try {
            const [accSnap, paramsSnap] = await Promise.all([
                getDocs(query(collection(db, ACCOUNTING_COL), orderBy('createdAt', 'desc'))),
                getDoc(doc(db, PARAMS_COL, 'system-settings'))
            ]);

            const records = accSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const parameters = paramsSnap.exists() ? paramsSnap.data() : {};

            return { records, parameters };
        } catch (error) {
            console.error("Error al obtener datos contables:", error);
            throw error;
        }
    },

    /**
     * Obtiene el email de pagos de un cliente específico.
     */
    getClientPaymentData: async (clientId) => {
        try {
            const clientDoc = await getDoc(doc(db, CLIENTS_COL, clientId));
            if (clientDoc.exists()) {
                const data = clientDoc.data();
                return { email: data.paymentEmail || data.email || '' };
            }
            return { email: '' };
        } catch (error) {
            console.error("Error obteniendo cliente:", error);
            return { email: '' };
        }
    },

    /**
     * Obtiene los gastos asociados a un folio específico (ej. gastos de un proyecto).
     */
    getRelatedExpenses: async (folioId) => {
        try {
            const q = query(collection(db, EXPENSES_COL), where('relatedFolio', '==', folioId));
            const snapshot = await getDocs(q);
            const expenses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Ordenar por fecha descendente
            return expenses.sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0));
        } catch (error) {
            console.error("Error obteniendo gastos relacionados:", error);
            throw error;
        }
    },

    /**
     * Genera el siguiente ID para cobros manuales (MAN-XXXXXXX).
     */
    generateManualId: async () => {
        const counterRef = doc(db, COUNTER_COL, 'counter');
        try {
            const newId = await runTransaction(db, async (transaction) => {
                const docSnap = await transaction.get(counterRef);
                if (!docSnap.exists()) {
                    transaction.set(counterRef, { current: 1 });
                    return 1;
                }
                const newCount = docSnap.data().current + 1;
                transaction.update(counterRef, { current: newCount });
                return newCount;
            });
            return `MAN-${String(newId).padStart(7, '0')}`;
        } catch (error) {
            console.error("Error generando ID manual:", error);
            throw error;
        }
    },

    /**
     * Crea un cobro manual.
     * También crea/actualiza el cliente en la base de datos de clientes si es necesario.
     */
    createManualRecord: async (recordData, clientData) => {
        try {
            // 1. Actualizar/Crear Cliente
            await setDoc(doc(db, CLIENTS_COL, clientData.rut), {
                nombre: clientData.name,
                rut: clientData.rut,
                updatedAt: serverTimestamp()
            }, { merge: true });

            // 2. Crear Registro Contable
            const newRecord = {
                ...recordData,
                createdAt: serverTimestamp()
            };
            
            // recordData.id debe venir generado por generateManualId previamente o aquí.
            // Asumimos que viene en recordData.id
            await setDoc(doc(db, ACCOUNTING_COL, recordData.id), newRecord);
            
            return recordData.id;
        } catch (error) {
            console.error("Error creando cobro manual:", error);
            throw error;
        }
    },

    /**
     * Actualiza un registro contable (facturas, pagos, estado).
     */
    updateRecord: async (id, data) => {
        try {
            await updateDoc(doc(db, ACCOUNTING_COL, id), {
                ...data,
                updatedAt: serverTimestamp()
            });
        } catch (error) {
            console.error("Error actualizando registro:", error);
            throw error;
        }
    },

    /**
     * Elimina un registro contable.
     */
    deleteRecord: async (id) => {
        try {
            await deleteDoc(doc(db, ACCOUNTING_COL, id));
        } catch (error) {
            console.error("Error eliminando registro:", error);
            throw error;
        }
    },

    /**
     * Sube archivos adjuntos (Facturas, Comprobantes).
     */
    uploadFile: async (file, recordId, folderType) => {
        if (!file) return null;
        try {
            const storage = getStorage();
            // accounting-docs/ID_REGISTRO/invoices/archivo.pdf
            const storagePath = `accounting-docs/${recordId}/${folderType}/${Date.now()}_${file.name}`;
            const storageRef = ref(storage, storagePath);
            await uploadBytes(storageRef, file);
            return await getDownloadURL(storageRef);
        } catch (error) {
            console.error("Error subiendo archivo:", error);
            throw error;
        }
    }
};